import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Car, LayoutDashboard, Plus, Users, Settings, LogOut, Menu, X, Globe, Inbox, Upload, Sparkles, Lock, Headphones, CalendarClock, TrendingUp } from 'lucide-react';
import { dealerAuth, clearDealerToken, getDealerToken, marketplaceActor } from '../../services/dealerApi';
import { usePlan } from '../../hooks/usePlan';

const navItems = [
  { to: '/dealers/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/dealers/inventory', label: 'Inventory', icon: Car },
  { to: '/dealers/add-car', label: 'Add Car', icon: Plus },
  { to: '/dealers/reels', label: 'AI Reels', icon: Sparkles, feature: 'reels_enabled' },
  { to: '/dealers/marketplace-leads', label: 'Marketplace Leads', icon: Upload, feature: 'marketplace_leads_enabled' },
  { to: '/dealers/pipeline', label: 'Pipeline', icon: TrendingUp, feature: 'marketplace_leads_enabled' },
  { to: '/dealers/telecallers', label: 'Telecallers', icon: Headphones, feature: 'marketplace_leads_enabled' },
  { to: '/dealers/leads', label: 'Buyer Leads', icon: Inbox },
  { to: '/dealers/customers', label: 'Customers', icon: Users },
  { to: '/dealers/profile', label: 'Profile', icon: Settings },
];

// Telecallers only get access to Marketplace Leads + My Day + their own Pipeline.
const TELECALLER_NAV = [
  { to: '/dealers/my-day', label: 'My Day', icon: CalendarClock },
  { to: '/dealers/marketplace-leads', label: 'Marketplace Leads', icon: Upload },
  { to: '/dealers/pipeline', label: 'My Pipeline', icon: TrendingUp },
];

const PUBLIC_PATHS = ['/dealers/login', '/dealers/register'];

const DealerLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dealer, setDealer] = useState(null);
  const [actorType, setActorType] = useState('dealer');
  const [telecaller, setTelecaller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { hasFeature } = usePlan();

  const isPublic = PUBLIC_PATHS.includes(location.pathname);
  const isTelecaller = actorType === 'telecaller';

  useEffect(() => {
    if (isPublic) { setLoading(false); return; }
    const token = getDealerToken();
    if (!token) {
      navigate('/dealers/login');
      return;
    }
    // Detect which kind of login this is via the marketplace /me probe.
    // NOTE: this endpoint sits behind the marketplace_leads_enabled feature
    // gate. Dealers on a plan without that feature receive a 403 — treat that
    // as "this is a plain dealer" instead of logging them out (which would
    // trap them in a login → 403 → logout loop).
    marketplaceActor.me()
      .then((meRes) => {
        if (meRes.actor_type === 'telecaller') {
          setActorType('telecaller');
          setTelecaller(meRes.telecaller);
          setDealer({ name: meRes.dealer_name, slug: '' });
          const allowed = ['/dealers/marketplace-leads', '/dealers/my-day', '/dealers/pipeline'];
          if (!allowed.some((p) => location.pathname.startsWith(p))) {
            navigate('/dealers/my-day', { replace: true });
          }
          return null; // done — telecaller path
        }
        return dealerAuth.me();
      })
      .catch(async (err) => {
        // Marketplace probe failed (usually 403 = feature disabled). Fall back
        // to the normal dealer identity fetch — do NOT clear the token here.
        const status = err?.response?.status;
        if (status === 401) {
          // Real auth failure — bounce to login.
          clearDealerToken();
          navigate('/dealers/login');
          throw err;
        }
        return dealerAuth.me();
      })
      .then((dRes) => {
        if (!dRes) return; // telecaller path already handled
        setDealer(dRes.dealer);
        if (!dRes.dealer.profile_completed && location.pathname !== '/dealers/profile') {
          navigate('/dealers/profile');
        }
      })
      .catch(() => {
        // Only reach here if BOTH probes failed with a hard auth error.
        clearDealerToken();
        navigate('/dealers/login');
      })
      .finally(() => setLoading(false));
  }, [isPublic, location.pathname, navigate]);

  const handleLogout = () => {
    clearDealerToken();
    navigate('/dealers/login');
  };

  if (isPublic) return <Outlet />;
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!dealer) return null;

  const items = isTelecaller ? TELECALLER_NAV : navItems;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-xl border-b border-white/5">
        {/* min-h-16 (was h-16) — grows on mobile when the telecaller
            sub-header ("Headphones name · dealer name") is longer than
            the row can hold. Prevents the sub-header from spilling out
            over the sticky edge and eating into the page content below. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-16 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 rounded-lg hover:bg-white/5 shrink-0" data-testid="dealer-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/brand/propitstop-drive.png"
                alt="Propitstop Drive"
                data-testid="propitstop-drive-logo"
                className="w-10 h-10 rounded-xl object-cover bg-slate-900 shadow-[0_0_15px_rgba(6,182,212,0.25)] shrink-0"
              />
              <div className="leading-tight min-w-0">
                <div className="font-extrabold text-white tracking-tight truncate">Propitstop Drive</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 truncate">
                  {isTelecaller ? (
                    <span className="inline-flex items-center gap-1 min-w-0"><Headphones className="w-3 h-3 shrink-0" /><span className="truncate">{telecaller?.name} · {dealer?.name}</span></span>
                  ) : (
                    dealer?.name
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isTelecaller && dealer.slug && (
              <a
                href={`/dealers/${dealer.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700 hover:border-cyan-500/40 text-sm text-slate-300 hover:text-cyan-300 transition-colors"
                data-testid="dealer-view-site"
              >
                <Globe className="w-4 h-4" /> View Site
              </a>
            )}
            <button
              onClick={handleLogout}
              data-testid="dealer-logout"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-300 hover:text-red-300 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Layout body — extra top padding on mobile so the "My Day" hero
          block never crowds the sticky Propitstop Drive logo header. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-6 pb-6 grid md:grid-cols-[220px,1fr] gap-6">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:block">
          <nav className="sticky top-20 space-y-1">
            {items.map((item) => {
              const locked = item.feature && !hasFeature(item.feature);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`dealer-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-cyan-500/10 text-cyan-300 border border-cyan-500/30'
                        : locked
                          ? 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" strokeWidth={1.75} />
                  <span className="flex-1">{item.label}</span>
                  {locked && <Lock className="w-3 h-3 text-slate-500" />}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Mobile drawer — z-50 so it sits ABOVE the sticky header (z-40).
            Prior z-30 caused the Propitstop logo to overlap the first nav
            item ("My Day") when the hamburger menu was opened. */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md" onClick={() => setMobileOpen(false)}>
            <nav className="bg-slate-900 w-64 h-full p-4 space-y-1" onClick={(e) => e.stopPropagation()}>
              {items.map((item) => {
                const locked = item.feature && !hasFeature(item.feature);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-cyan-500/10 text-cyan-300 border border-cyan-500/30'
                          : locked
                            ? 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`
                    }
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1">{item.label}</span>
                    {locked && <Lock className="w-3 h-3 text-slate-500" />}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        )}

        <main className="min-w-0">
          <Outlet context={{ dealer, actorType, telecaller, refreshDealer: () => dealerAuth.me().then((r) => setDealer(r.dealer)) }} />
        </main>
      </div>
    </div>
  );
};

export default DealerLayout;
